import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle, MapPin, Timer, Waves, Wind, Thermometer, Users, ShieldCheck,
  Bookmark, MessageSquarePlus, Info, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import ReportModal from "@/components/ReportModal";
import SurfMap from "@/components/SurfMap";
import WaterQualityPanel from "@/components/WaterQualityPanel";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { spotPath, countryPath, regionPath } from "@/lib/seo";

function fmt(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }); }
  catch { return iso; }
}

export default function SurfArea() {
  const { slug } = useParams();
  const { isAuthenticated } = useAuth();
  const [area, setArea] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [reports, setReports] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [saved, setSaved] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setErr("");
    api.get(`/surf-areas/${slug}`).then((r) => setArea(r.data)).catch(() => setErr("Area not found"));
  }, [slug]);

  useEffect(() => {
    if (!area) return;
    api.get(`/surf-areas/${slug}/forecast`).then((r) => setForecast(r.data)).catch(() => {});
    api.get(`/surf-areas/${slug}/reports`).then((r) => setReports(r.data.items || [])).catch(() => {});
    api.get(`/businesses?surf_area_id=${area.id}`).then((r) => setBusinesses(r.data.items || [])).catch(() => {});
    if (isAuthenticated) {
      api.get("/saved-areas").then((r) => setSaved((r.data.items || []).some((a) => a.id === area.id))).catch(() => {});
    }
  }, [area, isAuthenticated, slug]);

  const toggleSave = async () => {
    if (!isAuthenticated) { toast.info("Log in to save this area"); return; }
    try {
      if (saved) { await api.delete(`/saved-areas/${area.id}`); setSaved(false); toast.success("Removed from saved"); }
      else { await api.post("/saved-areas", { surf_area_id: area.id }); setSaved(true); toast.success("Saved"); }
    } catch { toast.error("Please try again"); }
  };

  const refetchReports = async () => {
    const r = await api.get(`/surf-areas/${slug}/reports`);
    setReports(r.data.items || []);
  };

  if (err) return <div className="max-w-4xl mx-auto p-8" data-testid="area-error">{err}</div>;
  if (!area) return <div className="max-w-4xl mx-auto p-8 text-muted-foreground">Loading…</div>;

  const hazards = area.safety?.general_hazards || [];
  const marine = forecast?.marine || [];
  const weather = forecast?.weather || [];
  const tideExtremes = forecast?.tide_extremes || [];

  return (
    <div data-testid="surf-area-page">
      <Seo
        title={`${area.name} — surf conditions & suitability`}
        description={
          (area.short_description || `Live conditions, tides and suitability for ${area.name}, ${area.region}, ${area.country}.`).slice(0, 300)
        }
        path={spotPath(area)}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Place",
          name: area.name,
          address: {
            "@type": "PostalAddress",
            addressRegion: area.region,
            addressCountry: area.country,
          },
          description: area.short_description,
        }}
      />
      <div className="max-w-6xl mx-auto px-5 md:px-8 pt-4">
        <Breadcrumbs
          items={[
            { label: "Surf spots", to: "/surf-spots" },
            { label: area.country, to: countryPath(area.country) },
            { label: area.region, to: regionPath(area.country, area.region) },
            { label: area.name },
          ]}
        />
      </div>
      {/* Hero */}
      <div className="relative">
        <div
          className="h-56 md:h-80 bg-cover bg-center"
          style={{ backgroundImage: `linear-gradient(180deg, rgba(0,58,67,0.1), rgba(0,58,67,0.55)), url(${area.image_url})` }}
        />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-6xl mx-auto w-full px-5 md:px-8 pb-8">
            <div className="text-white/85 text-xs uppercase tracking-widest">
              {area.country} · {area.region}
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-white mt-2" data-testid="area-name">
              {area.name}
            </h1>
            <div className="text-white/85 text-sm mt-1 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {area.nearest_town} · {area.break_type} break
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card-soft p-6 md:p-7">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">About</div>
                <h2 className="font-display text-2xl text-teal-900 mt-1">{area.short_description}</h2>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={toggleSave}
                  data-testid="area-save-btn"
                  className={`h-10 px-4 rounded-full text-sm border flex items-center gap-2 ${
                    saved ? "bg-teal-800 text-white border-teal-800" : "bg-white border-border text-teal-800"
                  }`}
                >
                  <Bookmark className="w-4 h-4" />
                  {saved ? "Saved" : "Save"}
                </button>
                <button
                  onClick={() => (isAuthenticated ? setReportOpen(true) : toast.info("Log in to submit a report"))}
                  data-testid="area-report-btn"
                  className="h-10 px-4 rounded-full text-sm bg-terracotta-500 text-white hover:bg-terracotta-600 flex items-center gap-2"
                >
                  <MessageSquarePlus className="w-4 h-4" />
                  Report conditions
                </button>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="overline">Beginners</div>
                <p className="mt-1 text-teal-900">{area.beginner_summary || "—"}</p>
              </div>
              <div>
                <div className="overline">Intermediate</div>
                <p className="mt-1 text-teal-900">{area.intermediate_summary || "—"}</p>
              </div>
            </div>
          </div>

          {/* Forecast */}
          <div className="card-soft p-6 md:p-7">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">Today's forecast</div>
                <h3 className="font-display text-xl text-teal-900 mt-1">Hour by hour</h3>
              </div>
              <div className="text-xs text-muted-foreground text-right">
                <div>Marine · {forecast?.marine_provider || "…"}</div>
                <div>Weather · {forecast?.weather_provider || "…"}</div>
              </div>
            </div>

            {forecast?.tide_is_mock && (
              <div className="mt-4 flex items-start gap-2 rounded-xl bg-[#FDF3F0] border border-[#F1D9CE] p-3 text-xs text-terracotta-700" data-testid="tide-mock-notice">
                <Info className="w-4 h-4 mt-0.5" />
                Tide values shown are a deterministic estimate for development and are not a live tide forecast. Configure a global tide provider (WorldTides) before commercial launch.
              </div>
            )}
            {forecast && !forecast.tide_is_mock && forecast.tide_available === false && (
              <div className="mt-4 flex items-start gap-2 rounded-xl bg-[#FDF3F0] border border-[#F1D9CE] p-3 text-xs text-terracotta-700" data-testid="tide-unavailable-notice">
                <Info className="w-4 h-4 mt-0.5" />
                Tide data unavailable for this location — recommendation confidence has been reduced accordingly.
              </div>
            )}
            {forecast && !forecast.tide_is_mock && forecast.tide_available && forecast.tide_station?.station_name && (
              <div className="mt-4 flex items-start gap-2 rounded-xl bg-sand-100 border border-border p-3 text-xs text-muted-foreground" data-testid="tide-source-notice">
                <Info className="w-4 h-4 mt-0.5" />
                Tide data via <b className="text-teal-900 mx-1">{forecast.tide_provider}</b>
                · station {forecast.tide_station.station_name}
                {forecast.tide_distance_km != null && <> ({forecast.tide_distance_km.toFixed(1)} km away)</>}
                {forecast.tide_note && <> · {forecast.tide_note}</>}
              </div>
            )}

            <div className="mt-4 -mx-2 overflow-x-auto no-scrollbar">
              <div className="flex gap-2 px-2 min-w-full">
                {marine.filter((_, i) => i % 2 === 0).slice(0, 12).map((h, i) => {
                  const w = weather[marine.indexOf(h)] || {};
                  return (
                    <div key={h.time} className="min-w-[110px] flex-shrink-0 rounded-2xl border border-border bg-white p-3" data-testid={`forecast-slot-${i}`}>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{fmt(h.time)}</div>
                      <div className="mt-2 flex items-center gap-1 text-teal-900">
                        <Waves className="w-3.5 h-3.5 text-teal-600" />
                        <span className="text-sm font-semibold">{h.wave_height_m?.toFixed(1)} m</span>
                      </div>
                      <div className="mt-1 flex items-center gap-1 text-teal-900">
                        <Wind className="w-3.5 h-3.5 text-teal-600" />
                        <span className="text-xs">{Math.round(w.wind_speed_kmh || 0)} km/h</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {tideExtremes.length > 0 && (
              <div className="mt-5">
                <div className="overline">Tide extremes (estimated)</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {tideExtremes.slice(0, 6).map((t) => (
                    <div key={t.time + t.type} className={`text-xs rounded-full px-3 py-1 border ${
                      t.type === "high" ? "bg-teal-800 text-white border-teal-800" : "bg-white border-border text-teal-800"
                    }`}>
                      {t.type === "high" ? "High" : "Low"} · {fmt(t.time)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Safety */}
          <div className="card-soft p-6 md:p-7">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-teal-800" />
              <h3 className="font-display text-xl text-teal-900">Safety at a glance</h3>
            </div>
            <p className="mt-2 text-sm text-teal-900">{area.safety_summary || "Standard beach hazards — assess conditions on arrival."}</p>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              {[
                ["Rip risk", area.safety?.rip_current_risk],
                ["Rocks/reef", area.safety?.rock_reef_risk],
                ["Shore break", area.safety?.shore_break_risk],
                ["Tidal cut-off", area.safety?.tidal_cutoff_risk],
              ].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-border p-3 bg-white">
                  <div className="overline">{k}</div>
                  <div className="mt-1 font-medium text-teal-900 capitalize">{v || "—"}</div>
                </div>
              ))}
            </div>
            {hazards.length > 0 && (
              <div className="mt-4 flex items-start gap-2 rounded-xl bg-[#FDF3F0] border border-[#F1D9CE] p-3">
                <AlertTriangle className="w-4 h-4 mt-0.5 text-terracotta-700 flex-shrink-0" />
                <div className="text-xs text-terracotta-700" data-testid="area-hazards">{hazards.join(" · ")}</div>
              </div>
            )}
            <div className="mt-3 text-xs text-muted-foreground">
              Lifeguard cover: {area.safety?.lifeguard_available ? area.safety?.lifeguard_details || "Seasonal" : "None recorded"}
            </div>
          </div>

          {/* Community */}
          <div className="card-soft p-6 md:p-7">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-teal-800" />
              <h3 className="font-display text-xl text-teal-900">Recent community reports</h3>
            </div>
            {reports.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground" data-testid="reports-empty">
                No reports yet in the last 24 hours. Be the first — tap "Report conditions".
              </p>
            ) : (
              <ul className="mt-4 space-y-3">
                {reports.map((r) => (
                  <li key={r.id} className="rounded-xl border border-border p-3 bg-white" data-testid={`report-${r.id}`}>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{r.user_name || "Anonymous surfer"}</span>
                      <span>{fmt(r.created_at)}</span>
                    </div>
                    <div className="mt-1 text-sm text-teal-900">
                      Quality: <b>{r.surf_quality}</b> · Crowd: <b>{r.crowd_level.replace("_", " ")}</b> · Waves: <b>{r.wave_category.replace("_", " ")}</b>
                    </div>
                    {r.comment && <div className="mt-1 text-sm text-teal-900">"{r.comment}"</div>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          <WaterQualityPanel slug={slug} />
          <div className="card-soft p-6">
            <div className="overline">Conditions preferences</div>
            <div className="mt-3 text-sm text-teal-900 space-y-2">
              <div><b>Preferred swell:</b> {(area.conditions?.preferred_swell_dir || []).join(", ") || "—"}</div>
              <div><b>Preferred wind:</b> {(area.conditions?.preferred_wind_dir || []).join(", ") || "—"}</div>
              <div><b>Poor wind:</b> {(area.conditions?.poor_wind_dir || []).join(", ") || "—"}</div>
              <div><b>Preferred tide:</b> {area.conditions?.preferred_tide || "any"}</div>
              <div><b>Typical water temp:</b> {area.typical_water_temp_c ? `${area.typical_water_temp_c} °C` : "—"}</div>
            </div>
          </div>

          <div className="card-soft p-6" data-testid="business-panel">
            <div className="overline">Local businesses</div>
            <h3 className="font-display text-xl text-teal-900 mt-1">Lessons & hire nearby</h3>
            {businesses.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">No listings yet — coming soon.</p>
            ) : (
              <ul className="mt-4 space-y-3">
                {businesses.slice(0, 4).map((b) => (
                  <li key={b.id}>
                    <Link
                      to={`/business/${b.id}`}
                      className="block rounded-xl border border-border p-3 hover:border-teal-800/50 bg-white"
                      data-testid={`business-panel-${b.id}`}
                    >
                      <div className="text-sm font-semibold text-teal-900">{b.name}</div>
                      <div className="text-xs text-muted-foreground capitalize">{b.business_type.replace(/_/g, " ")}</div>
                      <div className="mt-1 text-xs text-teal-800 flex items-center gap-1">
                        View & enquire <ChevronRight className="w-3 h-3" />
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {area.lat != null && area.lon != null && (
            <div className="card-soft p-3" data-testid="area-map">
              <SurfMap
                areas={[{
                  id: area.id,
                  slug: area.slug,
                  name: area.name,
                  lat: area.lat,
                  lon: area.lon,
                  visibility: area.visibility,
                  coordinates_approximate: area.coordinates_approximate,
                  coordinates_hidden: area.coordinates_hidden,
                }]}
                single
                height={280}
              />
              {area.coordinates_approximate && (
                <div className="text-[11px] text-terracotta-700 mt-2 px-2">
                  This is a broad surf area — the map shows an approximate location, not precise take-off spots.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {reportOpen && (
        <ReportModal
          surfAreaId={area.id}
          areaName={area.name}
          onClose={() => setReportOpen(false)}
          onSubmitted={() => { setReportOpen(false); refetchReports(); }}
        />
      )}
    </div>
  );
}
